import { LineChartAnomalyValue, TooltipPosition } from '@modules/charts/types';
import { getChartLabelIdOrName } from '@modules/charts/utils';
import { Category, GetChartLabelOptions } from '@modules/common/types';
import {
  formatNumToStandardNotation,
  toSentimentName,
} from '@modules/common/utils';
import { FeedbackListDrawer } from '@modules/feedback/components/FeedbackListDrawer';
import { useGetSelectedFeedbackMetadata } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { MergeSuggestionDetailsDrawer } from '@modules/taxonomy/components/MergeSuggestionDetailsDrawer';
import { EditReasonDrawer } from '@modules/taxonomy/components/Reason/EditReasonDrawer';
import { CircleRounded } from '@mui/icons-material';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { joinEntitySentimentPair } from 'components/QueryBuilderView/utils/entitySentimentWidgetHelper';
import { EqlField } from 'generated/graphql';
import { MouseEvent, ReactNode, useCallback, useEffect } from 'react';
import { TimeRange } from 'v2/core/entities';
import { FieldNames, SentimentType } from 'v2/core/entities/fields';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { decodeSourceFromString } from 'v2/core/services/queryBuilder/utils';
import {
  EmptyWhereClause,
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import {
  categoryWhereClauseGenerator,
  entitySentimentWhereClauseGenerator,
  getWhereClauseForFieldName,
} from 'v2/core/services/queryBuilder/whereClauseGenerator';
import TooltipWrapper from 'v2/views/components/charts/tooltip/TooltipWrapper';
import getFieldNameToDisplay from 'v2/views/components/charts/tooltip/getFieldNameToDisplay';
import { ChartTooltipContent } from './ChartTooltipContent';
import { ChartTooltipWrapperProvider } from './ChartTooltipWrapperProvider';
import { useChartTooltipStore } from './hooks';
import { useMergeSuggestions } from './hooks/useMergeSuggestions';

export interface TooltipPayload {
  id: string;
  label: string;
  value: number | null;
  entitySentiment?: SentimentType;
  timestamp?: number;
  color?: string;
  anomalyValue?: LineChartAnomalyValue;
  category?: Category;
  isInvisible?: boolean;
}

export interface ChartTooltipWrapperProps {
  baseWhereClause: WhereClause;
  renderHeader?: (payload: TooltipPayload | null) => ReactNode;
  showTooltip: boolean;
  setShowTooltip: (show: boolean) => void;
  tooltipPosition: TooltipPosition | null;
  setTooltipPosition: (position: TooltipPosition | null) => void;
  payload: TooltipPayload | null;
  fieldName?: FieldNames;
  qbField?: string;
  dateRange: TimeRange;
  shouldShowAddToFilter: boolean;
  shouldShowMergeReason?: boolean;
  shouldShowEditReason?: boolean;
  displayName?: string;
  getLabelOptions?: GetChartLabelOptions;
  onMergeComplete?: () => void;
  shouldExcludeDataPointWhereClause?: boolean;
  formulaID: EQLFunctionName;
  onFeedbackListDrawerClose?: () => Promise<void[]>;
  shouldHideOpenInSearchOnFeedbackDrawer?: boolean;
  measuredAsCriteria: EqlField | null;
  isSingleRecordQuery: boolean;
  formulaDisplayName?: string;
}

function ChartTooltip({
  baseWhereClause,
  payload,
  showTooltip,
  setShowTooltip,
  tooltipPosition,
  renderHeader,
  setTooltipPosition,
  fieldName,
  qbField,
  dateRange,
  shouldShowAddToFilter,
  shouldShowMergeReason,
  shouldShowEditReason,
  displayName,
  getLabelOptions,
  onMergeComplete,
  shouldExcludeDataPointWhereClause = false,
  formulaID,
  measuredAsCriteria,
  onFeedbackListDrawerClose,
  isSingleRecordQuery,
  formulaDisplayName,
  shouldHideOpenInSearchOnFeedbackDrawer = false,
}: ChartTooltipWrapperProps) {
  const setData = useChartTooltipStore(({ setData }) => setData);

  const feedbackQueryContainer = useChartTooltipStore(
    ({ feedbackQueryContainer }) => feedbackQueryContainer,
  );
  const shouldOpenFeedbackDrawer = useChartTooltipStore(
    ({ shouldOpenFeedbackDrawer }) => shouldOpenFeedbackDrawer,
  );
  const shouldShowConfirmMergeDrawer = useChartTooltipStore(
    ({ shouldShowConfirmMergeDrawer }) => shouldShowConfirmMergeDrawer,
  );
  const shouldShowEditReasonDrawer = useChartTooltipStore(
    ({ shouldShowEditReasonDrawer }) => shouldShowEditReasonDrawer,
  );

  const activeMergeSuggestion = useChartTooltipStore(
    ({ activeMergeSuggestion }) => activeMergeSuggestion,
  );

  const activeReason = useChartTooltipStore(({ activeReason }) => activeReason);

  const shouldRefetch = useFeedbackOnFeedbackStore(
    ({ shouldRefetch }) => shouldRefetch,
  );

  const {
    onAddSuggestion,
    onRemoveSuggestion,
    onUpdateCategory,
    onUpdateSuggestedName,
  } = useMergeSuggestions();

  useEffect(() => {
    setData({
      shouldShowAddToFilter,
    });
  }, [shouldShowAddToFilter, setData]);

  useEffect(() => {
    setData({
      measuredAsCriteria,
    });
  }, [measuredAsCriteria, setData]);

  useEffect(() => {
    setData({
      shouldShowMergeReason: shouldShowMergeReason ?? true,
    });
  }, [shouldShowMergeReason, setData]);

  useEffect(() => {
    setData({
      shouldShowEditReason: shouldShowEditReason ?? true,
    });
  }, [shouldShowEditReason, setData]);

  useEffect(() => {
    setData({
      baseWhereClause,
    });
  }, [baseWhereClause, setData]);

  useEffect(() => {
    setData({
      fieldName,
    });
  }, [fieldName, setData]);

  useEffect(() => {
    setData({
      qbField,
    });
  }, [qbField, setData]);

  useEffect(() => {
    setData({
      dateRange,
    });
  }, [dateRange, setData]);

  useEffect(() => {
    if (shouldOpenFeedbackDrawer) {
      setShowTooltip(false);
    }
  }, [shouldOpenFeedbackDrawer, setShowTooltip]);

  const getDataPointWhereClause = useCallback((): WhereClause => {
    if (shouldExcludeDataPointWhereClause || !payload || !fieldName) {
      return EmptyWhereClause;
    }

    const id = getChartLabelIdOrName(payload.id);

    if (payload.entitySentiment) {
      return entitySentimentWhereClauseGenerator([
        joinEntitySentimentPair(toSentimentName(payload.entitySentiment), id),
      ]);
    }

    const dataPointWhereClause = getWhereClauseForFieldName({
      fieldName,
      values:
        fieldName === FieldNames.Source ? [decodeSourceFromString(id)] : [id],
      op: OperationType.AND,
      qbField,
    });

    if (payload.category && fieldName === FieldNames.Entity) {
      const categoryFilter = categoryWhereClauseGenerator([payload.category]);
      return combineWhereClauses(
        dataPointWhereClause,
        categoryFilter,
        OperationType.AND,
      );
    }

    return dataPointWhereClause;
  }, [fieldName, payload, qbField, shouldExcludeDataPointWhereClause]);

  useEffect(() => {
    if (shouldShowConfirmMergeDrawer) {
      setShowTooltip(false);
    }
  }, [shouldShowConfirmMergeDrawer, setShowTooltip]);

  useEffect(() => {
    if (shouldShowEditReasonDrawer) {
      setShowTooltip(false);
    }
  }, [shouldShowEditReasonDrawer, setShowTooltip]);

  useEffect(() => {
    if (payload && fieldName) {
      setData({
        dataPointWhereClause: getDataPointWhereClause(),
        payload: {
          ...payload,
          id: getChartLabelIdOrName(payload.id),
          label: getChartLabelIdOrName(payload.label),
        },
      });
    }
  }, [fieldName, getDataPointWhereClause, payload, setData]);

  const onTooltipHover = (
    _: unknown,
    __: number | undefined,
    isActive: boolean,
    eve: MouseEvent<HTMLDivElement>,
  ) => {
    if (isActive) {
      setShowTooltip(true);
      setTooltipPosition({
        clientX: eve.clientX,
        clientY: eve.clientY,
      });
    } else {
      setShowTooltip(false);
      setTooltipPosition(null);
    }
  };

  const unitSymbol =
    formulaID === EQLFunctionName.NormaliseByPercent
      ? '%'
      : formulaID === EQLFunctionName.Count
      ? '#'
      : '';

  const tooltipHeaderOptions =
    payload &&
    getLabelOptions?.({
      id: payload.id,
      size: 14,
      showIndentForEmptySecondaryIcon: false,
      shouldHideTooltip: true,
    });

  const feedbackSidebarHeaderOptions =
    payload &&
    getLabelOptions?.({
      id: payload.id,
      size: 18,
      hideSecondaryIcon: true,
    });

  const getLabelTextMaxWidth = () => {
    let maxWidth = 270; // tooltip width
    if (payload?.color) {
      maxWidth -= 20;
    }
    if (tooltipHeaderOptions?.startIcon) {
      maxWidth -= 20;
    }
    if (tooltipHeaderOptions?.endIcon) {
      maxWidth -= 20;
    }
    return maxWidth;
  };

  const shouldShowFormulaName =
    !!formulaDisplayName &&
    formulaID !== EQLFunctionName.Count &&
    formulaID !== EQLFunctionName.NormaliseByPercent;

  const getCriteriaLabel = () => {
    if (measuredAsCriteria) {
      return shouldShowFormulaName
        ? `${formulaDisplayName} of ${measuredAsCriteria.displayName}`
        : measuredAsCriteria.displayName;
    }

    return 'Records';
  };

  const defaultRenderHeader = () => {
    return (
      <Stack direction="column" spacing={1} sx={{ px: 1.5, pt: 1 }}>
        <Stack direction="row" alignItems="flex-start" columnGap={0.5}>
          {payload?.color && (
            <CircleRounded
              sx={{
                width: 16,
                height: 16,
                color: payload.color,
                pt: 0.3,
              }}
            />
          )}
          {tooltipHeaderOptions?.startIcon && (
            <Box pt={0.3}>{tooltipHeaderOptions.startIcon}</Box>
          )}
          <Typography
            variant="body2"
            color={
              tooltipHeaderOptions?.isDisabled
                ? 'text.disabled'
                : 'text.primary'
            }
            sx={{
              maxWidth: `${getLabelTextMaxWidth()}px`,
              wordWrap: 'break-word',
            }}
          >
            {tooltipHeaderOptions?.label && `${tooltipHeaderOptions?.label}: `}
            {getChartLabelIdOrName(payload?.label)}
          </Typography>
          {tooltipHeaderOptions?.endIcon && (
            <Stack pt={0.3}>{tooltipHeaderOptions.endIcon}</Stack>
          )}
        </Stack>
        {tooltipHeaderOptions?.messageNode && tooltipHeaderOptions.messageNode}
        {payload?.anomalyValue && (
          <Stack direction="row" spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {unitSymbol} Expected {getCriteriaLabel()}:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formulaID === EQLFunctionName.NormaliseByPercent
                ? payload.anomalyValue.mean
                : Math.round(payload.anomalyValue.mean)}
            </Typography>
          </Stack>
        )}
        {payload?.value != null &&
          formulaID !== EQLFunctionName.NormaliseByPercent && (
            <Stack direction="row" spacing={0.5}>
              {isSingleRecordQuery ? null : (
                <Typography variant="body2" color="text.secondary">
                  {unitSymbol} {getCriteriaLabel()}:
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {payload?.value
                  ? formatNumToStandardNotation(payload.value)
                  : 0}
              </Typography>
            </Stack>
          )}
      </Stack>
    );
  };

  const selectedFeedbackMetadata = useGetSelectedFeedbackMetadata({
    whereClause: feedbackQueryContainer?.WhereClause,
    labels:
      payload && fieldName
        ? [
            {
              id: getChartLabelIdOrName(payload.id),
              fieldName,
              name: getChartLabelIdOrName(payload.label),
            },
          ]
        : undefined,
  });

  const onFeedbackDrawerClose = () => {
    if (shouldRefetch) {
      onFeedbackListDrawerClose?.();
    }
    setData({ shouldOpenFeedbackDrawer: false });
  };

  const shouldShowTooltip = showTooltip && !!payload && !!tooltipPosition;

  return shouldShowTooltip ||
    feedbackQueryContainer ||
    activeMergeSuggestion ||
    activeReason ? (
    <Box>
      {shouldShowTooltip && (
        <TooltipWrapper styles={tooltipPosition} onHoverHandle={onTooltipHover}>
          <ChartTooltipContent
            renderHeader={
              renderHeader ? () => renderHeader(payload) : defaultRenderHeader
            }
            shouldHideReasonActions={payload.isInvisible}
          />
        </TooltipWrapper>
      )}
      {feedbackQueryContainer && (
        <FeedbackListDrawer
          open={shouldOpenFeedbackDrawer}
          onClose={onFeedbackDrawerClose}
          queryContainer={feedbackQueryContainer}
          selectedFeedbackMetadata={selectedFeedbackMetadata}
          shouldHideOpenInSearch={shouldHideOpenInSearchOnFeedbackDrawer}
          header={{
            shouldEnableSort: true,
            renderTitle: () => {
              return (
                <Typography variant="caption">
                  Feedback with existing filters for
                </Typography>
              );
            },
            renderDescription: () => {
              if (!payload) {
                return;
              }

              return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {fieldName && (
                    <Typography variant="subtitle1" color="text.secondary">
                      {getFieldNameToDisplay(displayName ?? fieldName)}
                    </Typography>
                  )}
                  {isSingleRecordQuery ? null : (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle1">:</Typography>
                      {feedbackSidebarHeaderOptions?.startIcon && (
                        <>{feedbackSidebarHeaderOptions.startIcon}</>
                      )}
                      <Tooltip
                        arrow
                        title={feedbackSidebarHeaderOptions?.labelTooltipTitle}
                        placement="right"
                      >
                        <Typography
                          variant="subtitle1"
                          color={
                            feedbackSidebarHeaderOptions?.isDisabled
                              ? 'text.disabled'
                              : 'text.primary'
                          }
                        >
                          {getChartLabelIdOrName(payload.label)}
                        </Typography>
                      </Tooltip>
                      {feedbackSidebarHeaderOptions?.endIcon && (
                        <>{feedbackSidebarHeaderOptions.endIcon}</>
                      )}
                    </Stack>
                  )}
                </Stack>
              );
            },
          }}
        />
      )}
      {shouldShowConfirmMergeDrawer && !!activeMergeSuggestion && (
        <MergeSuggestionDetailsDrawer
          activeMergeSuggestion={activeMergeSuggestion}
          closeDrawer={() => setData({ shouldShowConfirmMergeDrawer: false })}
          title="Merge Reasons"
          onUpdateSuggestedName={onUpdateSuggestedName}
          onUpdateCategory={onUpdateCategory}
          onAddSuggestion={onAddSuggestion}
          onRemoveSuggestion={onRemoveSuggestion}
          onMergeComplete={() => onMergeComplete?.()}
          drawerSource="Quantify Chart"
        />
      )}
      {shouldShowEditReasonDrawer && activeReason && (
        <EditReasonDrawer
          isOpen={shouldShowEditReasonDrawer}
          onClose={() => {
            setData({
              activeReason: null,
              shouldShowEditReasonDrawer: false,
            });
          }}
          reasonID={activeReason.id}
          activeReasonData={activeReason}
        />
      )}
    </Box>
  ) : null;
}

export function ChartTooltipWrapper(props: ChartTooltipWrapperProps) {
  return (
    <ChartTooltipWrapperProvider>
      <ChartTooltip {...props} />
    </ChartTooltipWrapperProvider>
  );
}
